/**
 * Date formatting utilities with timezone support
 * 
 * All dates are stored in UTC in the database.
 * This utility converts UTC dates to the user's selected timezone for display.
 */

// Map of timezone IDs to display labels and offsets
export const TIMEZONE_OPTIONS = [
  // US Timezones
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: '-08:00' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', offset: '-07:00' },
  { value: 'America/Chicago', label: 'Central Time (CT)', offset: '-06:00' },
  { value: 'America/New_York', label: 'Eastern Time (ET)', offset: '-05:00' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)', offset: '-09:00' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)', offset: '-10:00' },
  // International
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', offset: '+00:00' },
  { value: 'Europe/London', label: 'London (GMT/BST)', offset: '+00:00' },
  { value: 'Europe/Paris', label: 'Central European Time (CET)', offset: '+01:00' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)', offset: '+09:00' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST)', offset: '+08:00' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)', offset: '+04:00' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)', offset: '+11:00' },
];

// Shared month abbreviations — hoisted to module scope so `case` blocks can
// reference it without `no-case-declarations` lint errors and so we keep a
// single source of truth.
const MONTH_ABBREV = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Date format options
export const DATE_FORMAT_OPTIONS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)', example: '12/25/2025' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)', example: '25/12/2025' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)', example: '2025-12-25' },
  { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY', example: 'Dec 25, 2025' },
  { value: 'DD MMM YYYY', label: 'DD MMM YYYY', example: '25 Dec 2025' },
];

/**
 * Format a UTC date string to the user's timezone
 * 
 * @param {string|Date} dateInput - ISO date string or Date object (assumed UTC)
 * @param {string} timezone - IANA timezone string (e.g., 'America/Los_Angeles')
 * @param {string} dateFormat - Date format string (e.g., 'MM/DD/YYYY')
 * @param {boolean} includeTime - Whether to include time in the output
 * @returns {string} Formatted date string in the user's timezone
 */
export function formatDateWithTimezone(dateInput, timezone = 'America/Los_Angeles', dateFormat = 'MM/DD/YYYY', includeTime = false) {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) return '';
    
    // Use Intl.DateTimeFormat for timezone conversion
    const options = {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(includeTime && {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      })
    };
    
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(date);
    
    // Extract parts
    const partMap = {};
    parts.forEach(p => {
      partMap[p.type] = p.value;
    });
    
    const month = partMap.month || '';
    const day = partMap.day || '';
    const year = partMap.year || '';
    const hour = partMap.hour || '';
    const minute = partMap.minute || '';
    const dayPeriod = partMap.dayPeriod || '';
    
    // Format date based on user preference
    let formattedDate = '';
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        formattedDate = `${day}/${month}/${year}`;
        break;
      case 'YYYY-MM-DD':
        formattedDate = `${year}-${month}-${day}`;
        break;
      case 'MMM DD, YYYY':
        formattedDate = `${MONTH_ABBREV[parseInt(month) - 1]} ${day}, ${year}`;
        break;
      case 'DD MMM YYYY':
        formattedDate = `${day} ${MONTH_ABBREV[parseInt(month) - 1]} ${year}`;
        break;
      case 'MM/DD/YYYY':
      default:
        formattedDate = `${month}/${day}/${year}`;
        break;
    }
    
    if (includeTime && hour && minute) {
      formattedDate += ` ${hour}:${minute} ${dayPeriod}`;
    }
    
    return formattedDate;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

/**
 * Format a date string for display (date only, no time)
 * Handles timezone-safe date parsing for date-only fields like LFD, Appointment Date
 * 
 * @param {string} dateStr - Date string in various formats
 * @param {string} dateFormat - Date format string (e.g., 'MM/DD/YYYY')
 * @returns {string} Formatted date string
 */
export function formatDateOnly(dateStr, dateFormat = 'MM/DD/YYYY') {
  if (!dateStr) return '';
  
  try {
    // If it's already in a simple format like "2025-03-18", parse without time
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-');
      switch (dateFormat) {
        case 'DD/MM/YYYY':
          return `${day}/${month}/${year}`;
        case 'YYYY-MM-DD':
          return dateStr;
        case 'MMM DD, YYYY':
          return `${MONTH_ABBREV[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
        case 'DD MMM YYYY':
          return `${parseInt(day)} ${MONTH_ABBREV[parseInt(month) - 1]} ${year}`;
        case 'MM/DD/YYYY':
        default:
          return `${month}/${day}/${year}`;
      }
    }
    
    // For ISO datetime strings, extract just the date portion and use UTC
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    
    // Use UTC methods to avoid timezone shift issues for date-only displays
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    
    switch (dateFormat) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'MMM DD, YYYY':
        return `${MONTH_ABBREV[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
      case 'DD MMM YYYY':
        return `${parseInt(day)} ${MONTH_ABBREV[parseInt(month) - 1]} ${year}`;
      case 'MM/DD/YYYY':
      default:
        return `${month}/${day}/${year}`;
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

/**
 * Format a datetime string for display (with time)
 * 
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {string} timezone - IANA timezone string
 * @param {string} dateFormat - Date format string
 * @returns {string} Formatted datetime string
 */
export function formatDateTime(dateInput, timezone = 'America/Los_Angeles', dateFormat = 'MM/DD/YYYY') {
  return formatDateWithTimezone(dateInput, timezone, dateFormat, true);
}

/**
 * Get relative time string (e.g., "2 hours ago", "in 3 days")
 * 
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {string} timezone - IANA timezone string (for reference)
 * @returns {string} Relative time string
 */
export function getRelativeTime(dateInput, _timezone = 'America/Los_Angeles') {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    const diffMs = date - now;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return diffDays === 1 ? 'in 1 day' : `in ${diffDays} days`;
    } else if (diffDays < 0) {
      const absDays = Math.abs(diffDays);
      return absDays === 1 ? '1 day ago' : `${absDays} days ago`;
    } else if (diffHours > 0) {
      return diffHours === 1 ? 'in 1 hour' : `in ${diffHours} hours`;
    } else if (diffHours < 0) {
      const absHours = Math.abs(diffHours);
      return absHours === 1 ? '1 hour ago' : `${absHours} hours ago`;
    } else if (diffMins > 0) {
      return diffMins === 1 ? 'in 1 minute' : `in ${diffMins} minutes`;
    } else if (diffMins < 0) {
      const absMins = Math.abs(diffMins);
      return absMins === 1 ? '1 minute ago' : `${absMins} minutes ago`;
    } else {
      return 'just now';
    }
  } catch (error) {
    console.error('Error calculating relative time:', error);
    return '';
  }
}

/**
 * Get current time in a specific timezone
 * 
 * @param {string} timezone - IANA timezone string
 * @returns {Date} Current time in the specified timezone
 */
export function getCurrentTimeInTimezone(timezone = 'America/Los_Angeles') {
  return new Date(new Date().toLocaleString('en-US', { timeZone: timezone }));
}

/**
 * Check if a date is today in the specified timezone
 * 
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {string} timezone - IANA timezone string
 * @returns {boolean}
 */
export function isToday(dateInput, timezone = 'America/Los_Angeles') {
  if (!dateInput) return false;
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    const now = new Date();
    
    const dateStr = date.toLocaleDateString('en-US', { timeZone: timezone });
    const nowStr = now.toLocaleDateString('en-US', { timeZone: timezone });
    
    return dateStr === nowStr;
  } catch (_error) {
    return false;
  }
}

/**
 * Check if a date is in the past in the specified timezone
 * 
 * @param {string|Date} dateInput - ISO date string or Date object
 * @param {string} timezone - IANA timezone string
 * @returns {boolean}
 */
export function isPast(dateInput, _timezone = 'America/Los_Angeles') {
  if (!dateInput) return false;
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    const now = new Date();
    return date < now;
  } catch (_error) {
    return false;
  }
}

/**
 * Convert a local date input to UTC ISO string for storage
 * Use this when saving dates from form inputs
 * 
 * @param {string} dateStr - Date string from input (e.g., "2025-03-20")
 * @param {string} timezone - The timezone the user is in
 * @returns {string} UTC ISO string
 */
export function localDateToUTC(dateStr, _timezone = 'America/Los_Angeles') {
  if (!dateStr) return '';
  
  try {
    // For date-only inputs, add noon time to avoid date shift issues
    const dateWithTime = dateStr.includes('T') ? dateStr : `${dateStr}T12:00:00`;
    
    // Create a date in the local timezone
    const localDate = new Date(dateWithTime);
    
    return localDate.toISOString();
  } catch (error) {
    console.error('Error converting to UTC:', error);
    return '';
  }
}

export default {
  TIMEZONE_OPTIONS,
  DATE_FORMAT_OPTIONS,
  formatDateWithTimezone,
  formatDateOnly,
  formatDateTime,
  getRelativeTime,
  getCurrentTimeInTimezone,
  isToday,
  isPast,
  localDateToUTC
};
